/**
 * Analytics consent UI.
 *
 * - `pending` (opt-in region, no choice yet): a compact bar with Accept / Decline.
 *   Nothing analytics-related has loaded; Accept loads GA4 + Clarity on the spot.
 * - `notice` (other regions, no choice yet): a small one-time notice with "Opt out";
 *   closing it sets 4k_notice=1 so it never returns.
 * Never inside an iframe, and never on share / player / learn / embed / blog routes or the
 * front door's /start, where it would cover the video or the sign-in — those apply the stored
 * choice or region default silently. The choice is shared with 4klabs.ai through the 4k_consent cookie.
 */

import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { PRIVACY_POLICY_URL, consentUi, isQuietRoute } from '../../analytics/consent';
import {
  dismissNotice,
  getConsentState,
  isNoticeDismissed,
  setConsentChoice,
  subscribeConsent,
} from '../../analytics/consentGate';

function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function ConsentBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The studio's composer runs along the bottom, with Stop and Send at its right end: stay above that row.
  const overComposer = pathname.startsWith('/chat/');
  const [state, setState] = useState(getConsentState);
  const [noticeDismissed, setNoticeDismissed] = useState(isNoticeDismissed);

  useEffect(
    () =>
      subscribeConsent((next) => {
        setState(next);
        setNoticeDismissed(isNoticeDismissed());
      }),
    [],
  );

  const ui = consentUi(state, {
    embedded: isEmbedded(),
    quietRoute: isQuietRoute(pathname),
    noticeDismissed,
  });

  if (ui === 'none') return null;

  const policyLink = (
    <a
      href={PRIVACY_POLICY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 hover:text-foreground"
    >
      Privacy policy
    </a>
  );

  if (ui === 'bar') {
    return (
      <div className={`pointer-events-none fixed inset-x-0 ${overComposer ? 'bottom-24' : 'bottom-0'} z-[90] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]`}>
        <section
          role="region"
          aria-label="Cookie consent"
          className="pointer-events-auto flex w-full max-w-2xl flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground shadow-lg sm:flex-row sm:items-center"
        >
          <p className="flex-1 text-sm text-muted-foreground">
            We'd like to use analytics cookies (Google Analytics, Microsoft Clarity) to understand how 4K Labs is
            used. They stay off unless you accept. {policyLink}
          </p>
          <div className="flex shrink-0 gap-2">
            <Button type="button" variant="outline" onClick={() => setConsentChoice('denied')}>
              Decline
            </Button>
            <Button type="button" onClick={() => setConsentChoice('granted')}>
              Accept
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-consent-notice className={`pointer-events-none fixed ${overComposer ? 'bottom-24' : 'bottom-4'} right-4 z-[90] max-w-[calc(100vw-2rem)] pb-[env(safe-area-inset-bottom)]`}>
      <section
        role="region"
        aria-label="Analytics notice"
        className="pointer-events-auto flex max-w-sm items-start gap-3 rounded-lg border bg-card p-3 text-card-foreground shadow-lg"
      >
        <p className="text-xs text-muted-foreground">
          We use analytics cookies to improve 4K Labs. {policyLink}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => setConsentChoice('denied')}>
          Opt out
        </Button>
        <button
          type="button"
          aria-label="Dismiss analytics notice"
          className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            dismissNotice();
            setNoticeDismissed(true);
          }}
        >
          <X className="h-4 w-4" />
        </button>
      </section>
    </div>
  );
}
